import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  Tabs,
  Form,
  Input,
  Button,
  Switch,
  Select,
  Avatar,
  message,
  Spin,
  Empty,
  Row,
  Col,
  Tag,
  Modal,
  Upload,
  Popconfirm,
  Descriptions,
  Space,
  Skeleton,
} from 'antd';
import {
  UserOutlined,
  IdcardOutlined,
  PhoneOutlined,
  MailOutlined,
  TeamOutlined,
  FileTextOutlined,
  FileImageOutlined,
  LinkOutlined,
  HomeOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  DownloadOutlined,
  UploadOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import {
  profileSettingsApi,
  userProfileApi,
  emergencyContactsApi,
  employeeDocsApi,
  show422Error,
} from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import PersianDatePicker from '../../components/PersianDatePicker';
import { FormSkeleton, CardListSkeleton } from '../../components/SkeletonLoaders';
import { normalizeEnglishDigits } from '../reports/reportUtils';

const GENDER_OPTIONS = [
  { value: 'male', label: 'مرد' },
  { value: 'female', label: 'زن' },
];

const MARITAL_OPTIONS = [
  { value: 'single', label: 'مجرد' },
  { value: 'married', label: 'متأهل' },
];

function formatFileSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} بایت`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} کیلوبایت`;
  return `${(n / (1024 * 1024)).toFixed(2)} مگابایت`;
}

function relationLabel(v) {
  if (v == null || v === '') return '—';
  return String(v);
}

function serializeBirthDate(v) {
  if (!v) return '';
  if (typeof v === 'string') {
    return normalizeEnglishDigits(v.trim()).replace(/-/g, '/');
  }
  if (typeof v.format === 'function') {
    try {
      const raw = v.format('YYYY/MM/DD');
      return normalizeEnglishDigits(raw);
    } catch {
      return '';
    }
  }
  return '';
}

function normFile(e) {
  if (Array.isArray(e)) return e;
  return e?.fileList;
}

function docIconNode(icon) {
  const s = String(icon || '').toLowerCase();
  if (s.includes('image') || s === 'images') return <FileImageOutlined className="text-xl" />;
  return <FileTextOutlined className="text-xl" />;
}

const PROFILE_TAB_KEYS = [
  { key: 'profile', label: 'پروفایل', icon: <IdcardOutlined /> },
  { key: 'emergency-contacts', label: 'تماس‌های اضطراری', icon: <PhoneOutlined /> },
  { key: 'documents', label: 'مدارک', icon: <FileTextOutlined /> },
];

const modalClassNames = {
  header: 'custom-font border-b border-gray-100 pb-3 dark:border-gray-700',
  body: 'custom-font',
};

const ProfileSettings = () => {
  const { user: authUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') || 'profile';
  const activeTab =
    tabFromUrl === 'emergency-contacts' || tabFromUrl === 'documents' ? tabFromUrl : 'profile';

  const [loading, setLoading] = useState(false);
  const [profileUser, setProfileUser] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [documentsMetaType, setDocumentsMetaType] = useState(null);

  const [profileForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [removeAvatar, setRemoveAvatar] = useState(false);

  const [contactForm] = Form.useForm();
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactModalMode, setContactModalMode] = useState('create');
  const [contactEditingId, setContactEditingId] = useState(null);
  const [contactSaving, setContactSaving] = useState(false);
  const [contactModalBusy, setContactModalBusy] = useState(false);
  const [contactDetailOpen, setContactDetailOpen] = useState(false);
  const [contactDetailLoading, setContactDetailLoading] = useState(false);
  const [contactDetail, setContactDetail] = useState(null);

  const [docForm] = Form.useForm();
  const [docNameForm] = Form.useForm();
  const [docUploadOpen, setDocUploadOpen] = useState(false);
  const [docUploading, setDocUploading] = useState(false);
  const [docNameModalOpen, setDocNameModalOpen] = useState(false);
  const [docEditingId, setDocEditingId] = useState(null);
  const [docNameSaving, setDocNameSaving] = useState(false);
  const [docDetailOpen, setDocDetailOpen] = useState(false);
  const [docDetailLoading, setDocDetailLoading] = useState(false);
  const [docDetail, setDocDetail] = useState(null);
  const [docDownloadLoadingId, setDocDownloadLoadingId] = useState(null);

  const currentUserId = useMemo(
    () => profileUser?.id ?? authUser?.id ?? documents[0]?.user_id ?? contacts[0]?.user_id,
    [profileUser?.id, authUser?.id, documents, contacts],
  );

  const fetchTab = useCallback(
    async (tabKey) => {
      setLoading(true);
      try {
        const res = await profileSettingsApi.getTab(tabKey);
        const data = res?.data ?? {};
        if (tabKey === 'profile') {
          const user = data?.user || null;
          setProfileUser(user);
          setRemoveAvatar(false);
          const ed = user?.employee_detail || {};
          profileForm.setFieldsValue({
            name: user?.name,
            email: user?.email,
            mobile: user?.mobile,
            gender: user?.gender || 'male',
            email_notifications: Number(user?.email_notifications) === 1,
            google_calendar_status: Number(user?.google_calendar_status) === 1,
            marital_status: ed?.marital_status || 'single',
            address: ed?.address || '',
            about_me: ed?.about_me || '',
            date_of_birth: ed?.date_of_birth || undefined,
            password: '',
          });
        } else if (tabKey === 'emergency-contacts') {
          setContacts(Array.isArray(data?.contacts) ? data.contacts : []);
        } else if (tabKey === 'documents') {
          setDocuments(Array.isArray(data?.docs) ? data.docs : []);
          setDocumentsMetaType(res?.type ?? data?.type ?? null);
        }
      } catch (e) {
        message.error(e?.message || 'خطا در دریافت اطلاعات');
      } finally {
        setLoading(false);
      }
    },
    [profileForm],
  );

  useEffect(() => {
    fetchTab(activeTab);
  }, [activeTab, fetchTab]);

  const onTabChange = (key) => {
    setSearchParams({ tab: key });
  };

  const onProfileSubmit = async (values) => {
    const uid = profileUser?.id;
    if (!uid) return;
    const payload = {
      name: values.name?.trim(),
      email: values.email?.trim(),
      mobile: values.mobile?.trim() ?? '',
      gender: values.gender,
      email_notifications: values.email_notifications ? 1 : 0,
      google_calendar_status: values.google_calendar_status ? 1 : 0,
      marital_status: values.marital_status,
      date_of_birth: serializeBirthDate(values.date_of_birth),
      address: values.address ?? '',
      about_me: values.about_me ?? '',
    };
    if (values.password && String(values.password).trim() !== '') {
      payload.password = values.password;
    }
    if (removeAvatar) {
      payload.image_delete = 'yes';
    }
    setSubmitting(true);
    try {
      await userProfileApi.update(uid, payload);
      message.success('پروفایل ذخیره شد');
      setRemoveAvatar(false);
      fetchTab('profile');
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, profileForm);
      else message.error(error?.message || 'خطا در ذخیره');
    } finally {
      setSubmitting(false);
    }
  };

  const openContactCreate = () => {
    setContactModalMode('create');
    setContactEditingId(null);
    contactForm.resetFields();
    setContactModalOpen(true);
  };

  const openContactEdit = async (id) => {
    setContactModalMode('edit');
    setContactEditingId(id);
    contactForm.resetFields();
    setContactModalBusy(true);
    setContactModalOpen(true);
    try {
      const res = await emergencyContactsApi.getById(id);
      const c = res?.data?.contact;
      if (!c) {
        message.warning('اطلاعات یافت نشد');
        setContactModalOpen(false);
        return;
      }
      contactForm.setFieldsValue({
        name: c.name,
        mobile: c.mobile,
        email: c.email ?? '',
        relationship: c.relation ?? c.relationship ?? '',
        address: c.address ?? '',
      });
    } catch (e) {
      message.error(e?.message || 'خطا در دریافت تماس');
      setContactModalOpen(false);
    } finally {
      setContactModalBusy(false);
    }
  };

  const submitContact = async () => {
    try {
      const v = await contactForm.validateFields();
      const payload = {
        name: v.name?.trim(),
        mobile: v.mobile?.trim(),
        email: (v.email && String(v.email).trim()) || '',
        relationship: v.relationship?.trim(),
        address: v.address ?? '',
      };
      setContactSaving(true);
      if (contactModalMode === 'create') {
        await emergencyContactsApi.create(payload);
        message.success('تماس اضطراری ثبت شد');
      } else if (contactEditingId) {
        await emergencyContactsApi.update(contactEditingId, payload);
        message.success('تماس بروزرسانی شد');
      }
      setContactModalOpen(false);
      fetchTab('emergency-contacts');
    } catch (err) {
      if (err?.errorFields) return;
      if (err?.name === 'ValidationError' || err?.details) show422Error(err, profileForm);
      else message.error(err?.message || 'خطا در ذخیره');
    } finally {
      setContactSaving(false);
    }
  };

  const deleteContact = async (id) => {
    try {
      await emergencyContactsApi.delete(id);
      message.success('تماس حذف شد');
      fetchTab('emergency-contacts');
    } catch (e) {
      message.error(e?.message || 'خطا در حذف');
    }
  };

  const openContactView = async (id) => {
    setContactDetailOpen(true);
    setContactDetailLoading(true);
    setContactDetail(null);
    try {
      const res = await emergencyContactsApi.getById(id);
      setContactDetail(res?.data?.contact ?? null);
    } catch (e) {
      message.error(e?.message || 'خطا در دریافت جزئیات');
      setContactDetailOpen(false);
    } finally {
      setContactDetailLoading(false);
    }
  };

  const openDocUpload = () => {
    if (!currentUserId) {
      message.warning('ابتدا اطلاعات پروفایل بارگذاری شود یا دوباره وارد شوید.');
      return;
    }
    docForm.resetFields();
    setDocUploadOpen(true);
  };

  const submitDocUpload = async () => {
    if (!currentUserId) return;
    try {
      const values = await docForm.validateFields();
      const list = values.file;
      const file = list?.[0]?.originFileObj ?? list?.[0];
      if (!file) {
        message.error('فایل را انتخاب کنید');
        return;
      }
      setDocUploading(true);
      await employeeDocsApi.create({
        name: values.name?.trim(),
        file,
        user_id: currentUserId,
      });
      message.success('مدرک بارگذاری شد');
      setDocUploadOpen(false);
      fetchTab('documents');
    } catch (err) {
      if (err?.errorFields) return;
      if (err?.name === 'ValidationError' || err?.details) show422Error(err, profileForm);
      else message.error(err?.message || 'خطا در بارگذاری');
    } finally {
      setDocUploading(false);
    }
  };

  const openDocNameEdit = (id) => {
    const row = documents.find((d) => d.id === id);
    setDocEditingId(id);
    docNameForm.setFieldsValue({ name: row?.name || '' });
    setDocNameModalOpen(true);
  };

  const submitDocName = async () => {
    if (!docEditingId) return;
    try {
      const v = await docNameForm.validateFields();
      setDocNameSaving(true);
      await employeeDocsApi.update(docEditingId, { name: v.name?.trim() });
      message.success('نام مدرک بروزرسانی شد');
      setDocNameModalOpen(false);
      fetchTab('documents');
    } catch (err) {
      if (err?.errorFields) return;
      if (err?.name === 'ValidationError' || err?.details) show422Error(err, profileForm);
      else message.error(err?.message || 'خطا در ذخیره');
    } finally {
      setDocNameSaving(false);
    }
  };

  const deleteDoc = async (id) => {
    try {
      await employeeDocsApi.delete(id);
      message.success('مدرک حذف شد');
      fetchTab('documents');
    } catch (e) {
      message.error(e?.message || 'خطا در حذف');
    }
  };

  const downloadDoc = async (id) => {
    if (id == null) return;
    setDocDownloadLoadingId(id);
    try {
      await employeeDocsApi.download(id);
    } catch (e) {
      message.error(e?.message || 'خطا در دانلود');
    } finally {
      setDocDownloadLoadingId(null);
    }
  };

  const openDocView = async (id) => {
    setDocDetailOpen(true);
    setDocDetailLoading(true);
    setDocDetail(null);
    try {
      const res = await employeeDocsApi.getById(id);
      setDocDetail(res?.data?.file ?? null);
    } catch (e) {
      message.error(e?.message || 'خطا در دریافت جزئیات');
      setDocDetailOpen(false);
    } finally {
      setDocDetailLoading(false);
    }
  };

  const avatarSrc = removeAvatar ? undefined : profileUser?.image_url;

  const tabBarItems = PROFILE_TAB_KEYS.map((t) => ({
    key: t.key,
    label: (
      <span className="inline-flex items-center gap-1 custom-font">
        {t.icon}
        <span>{t.label}</span>
      </span>
    ),
  }));

  const profileTabBusy = loading && activeTab === 'profile';
  const emergencyTabBusy = loading && activeTab === 'emergency-contacts';
  const documentsTabBusy = loading && activeTab === 'documents';

  const renderTabContent = () => {
    if (activeTab === 'profile') {
      if (profileTabBusy) {
        return (
          <Row gutter={[24, 24]} className="pt-2">
            <Col xs={24} lg={8}>
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900/70">
                <div className="flex flex-col items-center">
                  <Skeleton.Avatar active size={112} shape="circle" />
                  <Skeleton active className="mt-4 w-full" title={{ width: '40%' }} paragraph={{ rows: 2 }} />
                </div>
              </div>
            </Col>
            <Col xs={24} lg={16}>
              <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900/60">
                <FormSkeleton fields={9} />
              </div>
            </Col>
          </Row>
        );
      }
      if (!profileUser) {
        return <Empty description="اطلاعات پروفایل یافت نشد" />;
      }
      return (
        <Row gutter={[24, 24]} className="pt-2">
          <Col xs={24} lg={8}>
            <div className="rounded-2xl border border-gray-200 bg-gradient-to-b from-slate-50 to-white p-6 shadow-sm dark:border-gray-700 dark:from-slate-800/80 dark:to-slate-900">
              <div className="flex flex-col items-center text-center">
                <Avatar
                  size={112}
                  src={avatarSrc}
                  icon={<UserOutlined />}
                  className="mb-4 shadow-lg ring-4 ring-sky-100 dark:ring-sky-900/40"
                />
                <div className="text-lg font-semibold text-slate-800 dark:text-slate-100">{profileUser?.name}</div>
                <div className="mt-1 text-sm text-slate-500">{profileUser?.email}</div>
                <div className="mt-6 flex w-full flex-col gap-2">
                  <Button
                    danger={!removeAvatar}
                    type={removeAvatar ? 'primary' : 'default'}
                    ghost={!removeAvatar}
                    onClick={() => setRemoveAvatar((v) => !v)}
                  >
                    {removeAvatar ? 'انصراف از حذف تصویر' : 'حذف تصویر پروفایل'}
                  </Button>
                  <p className="m-0 text-xs leading-relaxed text-slate-400">
                    با ذخیرهٔ فرم، در صورت انتخاب حذف، تصویر از سرور پاک می‌شود.
                  </p>
                </div>
              </div>
            </div>
          </Col>
          <Col xs={24} lg={16}>
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900/60">
              <Form
                form={profileForm}
                layout="vertical"
                onFinish={onProfileSubmit}
                className="custom-font"
                requiredMark={false}
              >
                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="name" label="نام و نام خانوادگی" rules={[{ required: true, message: 'نام را وارد کنید' }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="email" label="ایمیل" rules={[{ required: true, type: 'email', message: 'ایمیل معتبر وارد کنید' }]}>
                      <Input dir="ltr" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="mobile" label="موبایل">
                      <Input dir="ltr" placeholder="09..." />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="gender" label="جنسیت" rules={[{ required: true }]}>
                      <Select options={GENDER_OPTIONS} popupClassName="custom-font" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="marital_status" label="وضعیت تأهل" rules={[{ required: true }]}>
                      <Select options={MARITAL_OPTIONS} popupClassName="custom-font" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="date_of_birth" label="تاریخ تولد">
                      <PersianDatePicker placeholder="انتخاب تاریخ تولد" />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item name="address" label="آدرس">
                      <Input.TextArea rows={2} placeholder="آدرس محل سکونت" />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item name="about_me" label="دربارهٔ من">
                      <Input.TextArea rows={4} placeholder="معرفی کوتاه…" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="password" label="رمز عبور جدید" extra="در صورت خالی ماندن، رمز عوض نمی‌شود">
                      <Input.Password dir="ltr" autoComplete="new-password" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} className="flex flex-col justify-end pb-6">
                    <div className="flex flex-wrap gap-6 rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800/80">
                      <Form.Item name="email_notifications" label="اعلان ایمیل" valuePropName="checked" className="mb-0">
                        <Switch />
                      </Form.Item>
                      <Form.Item name="google_calendar_status" label="تقویم گوگل" valuePropName="checked" className="mb-0">
                        <Switch />
                      </Form.Item>
                    </div>
                  </Col>
                  <Col xs={24}>
                    <Button type="primary" htmlType="submit" loading={submitting} className={PANEL_PRIMARY_BTN_CLASS} size="large">
                      ذخیرهٔ تغییرات
                    </Button>
                  </Col>
                </Row>
              </Form>
            </div>
          </Col>
        </Row>
      );
    }

    if (activeTab === 'emergency-contacts') {
      if (emergencyTabBusy) {
        return (
          <div className="pt-2">
            <div className="mb-4 flex justify-end">
              <Skeleton.Button active style={{ width: 140, height: 36 }} />
            </div>
            <CardListSkeleton count={6} />
          </div>
        );
      }
      return (
        <div className="pt-2">
          <div className="mb-4 flex justify-end">
            <Button type="primary" icon={<PlusOutlined />} className={PANEL_PRIMARY_BTN_CLASS} onClick={openContactCreate}>
              افزودن تماس
            </Button>
          </div>
          {contacts.length ? (
            <Row gutter={[16, 16]}>
              {contacts.map((c) => (
                <Col xs={24} sm={12} lg={8} key={c.id}>
                  <div className="flex h-full flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-900/70">
                    <div className="mb-3 flex items-start justify-between gap-2">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-600 dark:bg-rose-950/50 dark:text-rose-300">
                        <TeamOutlined className="text-lg" />
                      </div>
                      <Tag color="blue" className="m-0 border-0">
                        {relationLabel(c.relation)}
                      </Tag>
                    </div>
                    <div className="text-lg font-semibold text-slate-800 dark:text-slate-100">{c.name || '—'}</div>
                    <div className="mt-4 flex-1 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                      <div className="flex items-center gap-2">
                        <PhoneOutlined className="text-slate-400" />
                        <span dir="ltr">{c.mobile || '—'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MailOutlined className="text-slate-400" />
                        <span dir="ltr" className="truncate">
                          {c.email || '—'}
                        </span>
                      </div>
                      {c.address ? (
                        <div className="flex items-start gap-2">
                          <HomeOutlined className="mt-0.5 shrink-0 text-slate-400" />
                          <span>{c.address}</span>
                        </div>
                      ) : null}
                    </div>
                    <div className="mt-4 border-t border-slate-100 pt-3 dark:border-slate-700">
                      <Space size="small" wrap className="w-full justify-end">
                        <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => openContactView(c.id)}>
                          مشاهده
                        </Button>
                        <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openContactEdit(c.id)}>
                          ویرایش
                        </Button>
                        <Popconfirm title="حذف این تماس؟" okText="حذف" cancelText="لغو" onConfirm={() => deleteContact(c.id)}>
                          <Button type="text" size="small" danger icon={<DeleteOutlined />}>
                            حذف
                          </Button>
                        </Popconfirm>
                      </Space>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          ) : (
            <Empty description="تماس اضطراری ثبت نشده است" />
          )}
        </div>
      );
    }

    if (activeTab === 'documents') {
      if (documentsTabBusy) {
        return (
          <div className="pt-2">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <Skeleton.Input active style={{ width: 120, height: 24 }} />
              <Skeleton.Button active style={{ width: 140, height: 36 }} />
            </div>
            <CardListSkeleton count={6} />
          </div>
        );
      }
      return (
        <div className="pt-2">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            {documentsMetaType ? (
              <Tag>
                نوع: {documentsMetaType === 'employee' ? 'پرسنلی' : documentsMetaType}
              </Tag>
            ) : (
              <span />
            )}
            <Button type="primary" icon={<UploadOutlined />} className={PANEL_PRIMARY_BTN_CLASS} onClick={openDocUpload}>
              بارگذاری مدرک
            </Button>
          </div>
          {documents.length ? (
            <Row gutter={[16, 16]}>
              {documents.map((doc) => (
                <Col xs={24} sm={12} lg={8} key={doc.id}>
                  <div className="flex h-full flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-900/70">
                    <div className="mb-4 flex items-center gap-3">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-300">
                        {docIconNode(doc.icon)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-semibold text-slate-800 dark:text-slate-100">{doc.name || doc.filename}</div>
                        <div className="mt-0.5 text-xs text-slate-500">{formatFileSize(doc.size)}</div>
                      </div>
                    </div>
                    <div className="mt-auto border-t border-slate-100 pt-3 dark:border-slate-700">
                      <Space wrap size="small" className="w-full justify-end">
                        <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => openDocView(doc.id)}>
                          جزئیات
                        </Button>
                        <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openDocNameEdit(doc.id)}>
                          نام
                        </Button>
                        <Button
                          type="text"
                          size="small"
                          icon={<DownloadOutlined />}
                          loading={docDownloadLoadingId === doc.id}
                          onClick={() => downloadDoc(doc.id)}
                        >
                          دانلود
                        </Button>
                        <Button
                          type="link"
                          size="small"
                          href={doc.doc_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          icon={<LinkOutlined />}
                          className="!p-0 font-medium text-indigo-600 underline underline-offset-2 hover:!text-indigo-500 dark:!text-indigo-400 dark:hover:!text-indigo-300"
                        >
                          باز کردن
                        </Button>
                        <Popconfirm title="حذف این مدرک؟" okText="حذف" cancelText="لغو" onConfirm={() => deleteDoc(doc.id)}>
                          <Button type="text" size="small" danger icon={<DeleteOutlined />}>
                            حذف
                          </Button>
                        </Popconfirm>
                      </Space>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          ) : (
            <Empty description="مدرکی بارگذاری نشده است" />
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <MainLayout>
      <div className="custom-font">
        <Card className="overflow-hidden border-0 shadow-xl" styles={{ body: { padding: 0 } }}>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white px-6 py-6 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-50 text-sky-600 dark:bg-sky-900/30 dark:text-sky-300">
                <UserOutlined className="text-xl" />
              </div>
              <div>
                <h1 className="m-0 text-xl font-semibold text-gray-900 dark:text-gray-100">تنظیمات پروفایل</h1>
                <p className="m-0 mt-1 max-w-xl text-sm text-gray-500 dark:text-gray-400">اطلاعات شخصی، تماس‌های اضطراری و مدارک</p>
              </div>
            </div>
            <Button icon={<ReloadOutlined />} loading={loading} onClick={() => fetchTab(activeTab)}>
              بروزرسانی
            </Button>
          </div>

          <div className="bg-gray-50 px-4 py-6 dark:bg-gray-900/50 sm:px-6">
            <Tabs type="card" activeKey={activeTab} onChange={onTabChange} items={tabBarItems} />
            <div className="mt-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900/80 sm:p-6">
              {renderTabContent()}
            </div>
          </div>
        </Card>

        <Modal
          title={
            <span className="text-lg font-semibold text-slate-800 dark:text-slate-100">
              {contactModalMode === 'create' ? 'افزودن تماس اضطراری' : 'ویرایش تماس اضطراری'}
            </span>
          }
          open={contactModalOpen}
          onCancel={() => setContactModalOpen(false)}
          onOk={submitContact}
          okText="ذخیره"
          cancelText="لغو"
          confirmLoading={contactSaving}
          okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
          destroyOnClose
          width={520}
          classNames={modalClassNames}
        >
          <Spin spinning={contactModalBusy}>
            <Form form={contactForm} layout="vertical" className="custom-font mt-2">
              <Form.Item name="name" label="نام" rules={[{ required: true, message: 'نام را وارد کنید' }]}>
                <Input />
              </Form.Item>
              <Form.Item name="mobile" label="موبایل" rules={[{ required: true, message: 'موبایل را وارد کنید' }]}>
                <Input dir="ltr" placeholder="09..." />
              </Form.Item>
              <Form.Item
                name="email"
                label="ایمیل"
                normalize={(v) => (v && String(v).trim() ? String(v).trim() : undefined)}
                rules={[{ type: 'email', message: 'ایمیل معتبر وارد کنید' }]}
              >
                <Input dir="ltr" placeholder="اختیاری" />
              </Form.Item>
              <Form.Item name="relationship" label="نسبت" rules={[{ required: true, message: 'نسبت را وارد کنید' }]}>
                <Input placeholder="مثال: همسر، والد، دوست" />
              </Form.Item>
              <Form.Item name="address" label="آدرس">
                <Input.TextArea rows={3} placeholder="اختیاری" />
              </Form.Item>
            </Form>
          </Spin>
        </Modal>

        <Modal
          title={<span className="text-lg font-semibold text-slate-800 dark:text-slate-100">جزئیات تماس اضطراری</span>}
          open={contactDetailOpen}
          onCancel={() => setContactDetailOpen(false)}
          footer={
            <Button type="primary" className={PANEL_PRIMARY_BTN_CLASS} onClick={() => setContactDetailOpen(false)}>
              بستن
            </Button>
          }
          destroyOnClose
          width={480}
          classNames={modalClassNames}
        >
          {contactDetailLoading ? (
            <div className="py-6">
              <Skeleton active paragraph={{ rows: 5 }} />
            </div>
          ) : contactDetail ? (
            <Descriptions bordered column={1} size="small" className="custom-font mt-2">
              <Descriptions.Item label="نام">{contactDetail.name ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="موبایل">
                <span dir="ltr">{contactDetail.mobile ?? '—'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="ایمیل">
                <span dir="ltr">{contactDetail.email ?? '—'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="نسبت">{relationLabel(contactDetail.relation ?? contactDetail.relationship)}</Descriptions.Item>
              <Descriptions.Item label="آدرس">{contactDetail.address ?? '—'}</Descriptions.Item>
            </Descriptions>
          ) : (
            <Empty />
          )}
        </Modal>

        <Modal
          title={<span className="text-lg font-semibold text-slate-800 dark:text-slate-100">بارگذاری مدرک</span>}
          open={docUploadOpen}
          onCancel={() => setDocUploadOpen(false)}
          onOk={submitDocUpload}
          okText="بارگذاری"
          cancelText="لغو"
          confirmLoading={docUploading}
          okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
          destroyOnClose
          width={520}
          classNames={modalClassNames}
        >
          <Form form={docForm} layout="vertical" className="custom-font mt-2">
            <Form.Item name="name" label="عنوان مدرک" rules={[{ required: true, message: 'عنوان را وارد کنید' }]}>
              <Input placeholder="مثال: کارت ملی" />
            </Form.Item>
            <Form.Item
              name="file"
              label="فایل"
              valuePropName="fileList"
              getValueFromEvent={normFile}
              rules={[{ required: true, message: 'فایل را انتخاب کنید' }]}
            >
              <Upload maxCount={1} beforeUpload={() => false} showUploadList>
                <Button icon={<UploadOutlined />}>انتخاب فایل</Button>
              </Upload>
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title={<span className="text-lg font-semibold text-slate-800 dark:text-slate-100">ویرایش نام مدرک</span>}
          open={docNameModalOpen}
          onCancel={() => setDocNameModalOpen(false)}
          onOk={submitDocName}
          okText="ذخیره"
          cancelText="لغو"
          confirmLoading={docNameSaving}
          okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
          destroyOnClose
          width={440}
          classNames={modalClassNames}
        >
          <Form form={docNameForm} layout="vertical" className="custom-font mt-2">
            <Form.Item name="name" label="عنوان" rules={[{ required: true, message: 'عنوان را وارد کنید' }]}>
              <Input />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title={<span className="text-lg font-semibold text-slate-800 dark:text-slate-100">جزئیات مدرک</span>}
          open={docDetailOpen}
          onCancel={() => setDocDetailOpen(false)}
          footer={
            <Space>
              {docDetail?.id ? (
                <Button
                  icon={<DownloadOutlined />}
                  loading={docDownloadLoadingId === docDetail.id}
                  onClick={() => downloadDoc(docDetail.id)}
                >
                  دانلود از سرور
                </Button>
              ) : null}
              <Button type="primary" className={PANEL_PRIMARY_BTN_CLASS} onClick={() => setDocDetailOpen(false)}>
                بستن
              </Button>
            </Space>
          }
          destroyOnClose
          width={520}
          classNames={modalClassNames}
        >
          {docDetailLoading ? (
            <div className="py-6">
              <Skeleton active paragraph={{ rows: 4 }} />
            </div>
          ) : docDetail ? (
            <Descriptions bordered column={1} size="small" className="custom-font mt-2">
              <Descriptions.Item label="عنوان">{docDetail.name ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="نام فایل">{docDetail.filename ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="حجم">{formatFileSize(docDetail.size)}</Descriptions.Item>
              <Descriptions.Item label="لینک">
                {docDetail.doc_url ? (
                  <a
                    href={docDetail.doc_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-indigo-600 underline underline-offset-2 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
                  >
                    باز کردن فایل
                  </a>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <Empty />
          )}
        </Modal>
      </div>
    </MainLayout>
  );
};

export default ProfileSettings;
